"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyEventPatchToDesign = applyEventPatchToDesign;
/**
 * `applyEventPatchToDesign` — the design-pass twin of the PassViewModel
 * deep-merge update. Translates the flight-event `PassViewModelPatch` the
 * sqsHandler / prepSweep already emit into value-level edits on a persisted
 * `PassDesign`:
 *
 *   flight.gate/terminal  → value of any field with the conventional key
 *                           (+ semantics.departureGate/-Terminal when a
 *                           semantics block exists)
 *   flight.status(+delay) → value of any `status` field via flightStatusText
 *   notice                → upsert the `notice` back-field (changeMessage %@ →
 *                           lock-screen banner)
 *   barcode (non-empty)   → the conditional delay-QR; an ABSENT barcode patch
 *                           never touches an operator-designed barcode
 *
 * VALUES only — the design owns the field set, order, labels and formatters.
 * Null policy: an explicit `gate: null` / `terminal: null` patch is a
 * KEEP-LAST-KNOWN no-op on the pass face (unlike `mergePassDesign`'s
 * null-deletes convention) — deliberate, so flight-event flapping never
 * blanks an already-shown value.
 * Pure: never mutates the input.
 */
const airportTimeZones_js_1 = require("./airportTimeZones.js");
const flightStatusText_js_1 = require("./flightStatusText.js");
const GROUPS = ['header', 'primary', 'secondary', 'auxiliary', 'back'];
function setValueByKey(design, key, value) {
    let changed = false;
    const fields = {};
    for (const group of GROUPS) {
        const rows = design.fields[group];
        if (rows === undefined)
            continue;
        fields[group] = rows.map((f) => {
            if (f.key !== key || f.value === value)
                return f;
            changed = true;
            return { ...f, value };
        });
    }
    return changed ? { ...design, fields } : design;
}
function applyEventPatchToDesign(design, patch) {
    let next = design;
    const flight = patch.flight;
    if (flight !== undefined) {
        // Each `flight.*` value is captured into a local const the moment it's
        // narrowed — re-reading a dotted property access after an intervening
        // function call (e.g. `flight.gate` after `setValueByKey(...)`) is not
        // reliably narrowed by TS, so the local binding is used throughout
        // instead of the property access.
        const gate = flight.gate;
        if (typeof gate === 'string' && gate !== '') {
            next = setValueByKey(next, 'gate', gate);
            if (next.semantics !== undefined) {
                next = { ...next, semantics: { ...next.semantics, departureGate: gate } };
            }
        }
        const terminal = flight.terminal;
        if (typeof terminal === 'string' && terminal !== '') {
            next = setValueByKey(next, 'terminal', terminal);
            if (next.semantics !== undefined) {
                next = { ...next, semantics: { ...next.semantics, departureTerminal: terminal } };
            }
        }
        const status = flight.status;
        if (status !== undefined) {
            // Movement statuses carry the wall time when the patch has the actual
            // instant AND the design knows whose clock to read (the airport codes
            // seeded into semantics). Unknown zone → no time: 'Leszállt' beats a
            // Budapest-clock time on a Tel Aviv arrival.
            const instant = status === 'LANDED'
                ? flight.actualArrival
                : status === 'DEPARTED'
                    ? flight.actualDeparture
                    : undefined;
            const iata = status === 'LANDED'
                ? next.semantics?.destinationAirportCode
                : next.semantics?.departureAirportCode;
            const timeText = typeof instant === 'string' && instant !== '' && iata !== undefined
                ? (0, airportTimeZones_js_1.airportLocalTime)(instant, iata)
                : null;
            next = setValueByKey(next, 'status', (0, flightStatusText_js_1.flightStatusText)(status, flight.delayMinutes ?? 0, timeText ?? undefined));
        }
        const scheduledDeparture = flight.scheduledDeparture;
        if (typeof scheduledDeparture === 'string' && scheduledDeparture !== '') {
            if (next.semantics !== undefined) {
                // No producer sends scheduledDeparture today; if one ever does, the
                // visible 'departure' field will NOT track it (face vs Smart-Stack
                // divergence) — revisit then.
                next = {
                    ...next,
                    semantics: { ...next.semantics, currentDepartureDate: scheduledDeparture },
                };
            }
        }
    }
    const notice = patch.notice;
    if (typeof notice === 'string' && notice !== '') {
        const back = [...(next.fields.back ?? [])];
        const i = back.findIndex((f) => f.key === 'notice');
        const existing = i >= 0 ? back[i] : undefined;
        if (existing !== undefined) {
            back[i] = {
                ...existing,
                value: notice,
                changeMessage: existing.changeMessage ?? '%@',
            };
        }
        else {
            const fresh = {
                key: 'notice',
                label: 'Értesítés',
                value: notice,
                changeMessage: '%@',
            };
            back.push(fresh);
        }
        next = { ...next, fields: { ...next.fields, back } };
    }
    const barcodePatch = patch.barcode;
    if (barcodePatch !== undefined) {
        const message = barcodePatch.value;
        if (typeof message === 'string' && message !== '') {
            const altText = barcodePatch.altText;
            next = {
                ...next,
                barcode: {
                    format: 'PKBarcodeFormatQR',
                    message,
                    ...(typeof altText === 'string' && altText !== '' ? { altText } : {}),
                },
            };
        }
    }
    return next;
}
//# sourceMappingURL=applyEventPatchToDesign.js.map